import React, { useEffect, useMemo, useRef, useState } from "react";
import { Helmet } from "react-helmet";
import urls from "../../Utils/urls";
import { allBlog } from "../../Utils/api";
import BlogCard from "./BlogCard";
import "../../css/Common.css";
import "./BlogAll.css";

const POSTS_PER_PAGE = 6;

const IcoSearch = () => (
  <svg width="17" height="17" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <circle cx="11" cy="11" r="8" />
    <line x1="21" y1="21" x2="16.65" y2="16.65" />
  </svg>
);

const IcoChevron = ({ flip }) => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true" style={flip ? { transform: "rotate(180deg)" } : undefined}>
    <polyline points="15 18 9 12 15 6" />
  </svg>
);

const BlogSkeleton = () => (
  <div className="blog-skeleton" aria-hidden="true">
    <div className="blog-skeleton-media" />
    <div className="blog-skeleton-body">
      <span className="blog-skeleton-line blog-skeleton-line--sm" />
      <span className="blog-skeleton-line" />
      <span className="blog-skeleton-line" />
      <span className="blog-skeleton-line blog-skeleton-line--md" />
    </div>
  </div>
);

const BlogAll = () => {
  const [blogList, setBlogList] = useState([]);
  const [status, setStatus] = useState("loading"); // loading | ready | error
  const [currentPage, setCurrentPage] = useState(1);
  const [search, setSearch] = useState("");
  const [category, setCategory] = useState("All");
  const gridRef = useRef(null);

  useEffect(() => {
    let active = true;

    allBlog({ start: "0", end: "100" })
      .then((resp) => {
        if (!active) return;
        if (resp?.data?.Status === 1 && Array.isArray(resp.data.data)) {
          setBlogList(resp.data.data);
          setStatus("ready");
        } else if (resp?.data?.Status === 0) {
          // "No data available." -- a valid empty list, not a failure.
          setBlogList([]);
          setStatus("ready");
        } else {
          setStatus("error");
        }
      })
      .catch(() => {
        if (active) setStatus("error");
      });

    return () => {
      active = false;
    };
  }, []);

  const categories = useMemo(() => {
    const found = blogList
      .map((item) => (item.category_name || "").trim())
      .filter(Boolean);
    return ["All", ...Array.from(new Set(found))];
  }, [blogList]);

  const filtered = useMemo(() => {
    const term = search.trim().toLowerCase();
    return blogList.filter((item) => {
      if (category !== "All" && (item.category_name || "").trim() !== category) {
        return false;
      }
      if (!term) return true;
      return `${item.title || ""} ${item.short_description || ""} ${item.seo_keyword || ""}`
        .toLowerCase()
        .includes(term);
    });
  }, [blogList, search, category]);

  // Any filter change invalidates the current page number.
  useEffect(() => {
    setCurrentPage(1);
  }, [search, category]);

  const totalPages = Math.max(1, Math.ceil(filtered.length / POSTS_PER_PAGE));
  const safePage = Math.min(currentPage, totalPages);
  const start = (safePage - 1) * POSTS_PER_PAGE;
  const currentPosts = filtered.slice(start, start + POSTS_PER_PAGE);

  // The featured slot only makes sense on an unfiltered first page.
  const isDefaultView = safePage === 1 && category === "All" && !search.trim();
  const featured = isDefaultView && currentPosts.length > 1 ? currentPosts[0] : null;
  const gridPosts = featured ? currentPosts.slice(1) : currentPosts;

  const goToPage = (page) => {
    setCurrentPage(page);
    if (gridRef.current) {
      gridRef.current.scrollIntoView({ behavior: "smooth", block: "start" });
    }
  };

  const metaDescription =
    "Stay updated with expert insights, tips and guides on instant salary loans, personal finance and money management. Explore the latest articles from Salary Uncle.";

  return (
    <>
      <Helmet>
        <title>Latest Blogs on Salary Loans &amp; Financial Tips | Salary Uncle</title>
        <meta name="description" content={metaDescription} />
        <meta property="og:type" content="website" />
        <meta property="og:title" content="Latest Blogs on Salary Loans & Financial Tips | Salary Uncle" />
        <meta property="og:description" content={metaDescription} />
        <meta property="og:url" content={`${urls.SITE_URL}/blog`} />
        <meta name="twitter:card" content="summary_large_image" />
        <link rel="canonical" href={`${urls.SITE_URL}/blog`} />
      </Helmet>

      {/* ── Hero ── */}
      <section className="blog-hero">
        <div className="blog-hero-inner">
          <span className="blog-hero-tag">Salary Uncle Blog</span>
          <h1>Money advice that actually fits your payday</h1>
          <p>
            Practical guides on salary advances, eligibility, credit scores and everyday
            money management &mdash; written in plain language, no jargon.
          </p>
        </div>
      </section>

      {/* ── Body ── */}
      <section className="blog-wrapper">
        <div className="blog-inner">
          {status === "ready" && blogList.length > 0 && (
            <div className="blog-toolbar">
              <div className="blog-search">
                <span className="blog-search-icon"><IcoSearch /></span>
                <input
                  type="search"
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  placeholder="Search articles..."
                  aria-label="Search articles"
                />
              </div>

              {categories.length > 1 && (
                <div className="blog-chips" role="group" aria-label="Filter by category">
                  {categories.map((name) => (
                    <button
                      key={name}
                      type="button"
                      className={category === name ? "blog-chip is-active" : "blog-chip"}
                      onClick={() => setCategory(name)}
                      aria-pressed={category === name}
                    >
                      {name}
                    </button>
                  ))}
                </div>
              )}
            </div>
          )}

          <div ref={gridRef}>
            {status === "loading" && (
              <div className="blog-grid" aria-busy="true" aria-label="Loading articles">
                {Array.from({ length: 6 }).map((_, i) => (
                  <BlogSkeleton key={i} />
                ))}
              </div>
            )}

            {status === "error" && (
              <div className="blog-state">
                <h2>We couldn&rsquo;t load the articles</h2>
                <p>Something went wrong on our end. Please refresh the page and try again.</p>
                <button type="button" className="blog-state-btn" onClick={() => window.location.reload()}>
                  Retry
                </button>
              </div>
            )}

            {status === "ready" && filtered.length === 0 && (
              <div className="blog-state">
                <h2>No articles found</h2>
                <p>
                  {blogList.length === 0
                    ? "We're working on fresh content. Check back soon."
                    : "Try a different search term or category."}
                </p>
                {blogList.length > 0 && (
                  <button
                    type="button"
                    className="blog-state-btn"
                    onClick={() => {
                      setSearch("");
                      setCategory("All");
                    }}
                  >
                    Clear filters
                  </button>
                )}
              </div>
            )}

            {status === "ready" && filtered.length > 0 && (
              <>
                {featured && (
                  <div className="blog-featured">
                    <BlogCard blog={featured} featured />
                  </div>
                )}

                {gridPosts.length > 0 && (
                  <div className="blog-grid">
                    {gridPosts.map((item) => (
                      <BlogCard key={item.id || item.slug} blog={item} />
                    ))}
                  </div>
                )}
              </>
            )}
          </div>

          {status === "ready" && totalPages > 1 && (
            <nav className="blog-pagination" aria-label="Blog pagination">
              <button
                type="button"
                className="blog-page-btn blog-page-btn--nav"
                onClick={() => goToPage(safePage - 1)}
                disabled={safePage === 1}
                aria-label="Previous page"
              >
                <IcoChevron />
              </button>

              {Array.from({ length: totalPages }).map((_, i) => (
                <button
                  key={i + 1}
                  type="button"
                  className={safePage === i + 1 ? "blog-page-btn is-active" : "blog-page-btn"}
                  onClick={() => goToPage(i + 1)}
                  aria-current={safePage === i + 1 ? "page" : undefined}
                >
                  {i + 1}
                </button>
              ))}

              <button
                type="button"
                className="blog-page-btn blog-page-btn--nav"
                onClick={() => goToPage(safePage + 1)}
                disabled={safePage === totalPages}
                aria-label="Next page"
              >
                <IcoChevron flip />
              </button>
            </nav>
          )}
        </div>
      </section>
    </>
  );
};

export default BlogAll;
